import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UploadCloud, FileSpreadsheet, ArrowRight, CheckCircle2, AlertTriangle, XCircle, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PicturesImport } from "@/components/import/PicturesImport";
import { Checkbox } from "@/components/ui/checkbox";

// Reusable QuickBooks migration wizard. `base` is the API prefix:
//  - Owner self-serve: "/migration"
//  - Super Admin assist: `/superadmin/clients/${cid}/migration`
export function MigrationWizard({ base = "/migration", tenantLabel }) {
  const [step, setStep] = useState(1);
  const [dataType, setDataType] = useState("products");
  // Import types come from the backend Import Type Registry (module-aware); labels unchanged.
  const [importTypes, setImportTypes] = useState([{ key: "products", name: "Products / Items" }, { key: "customers", name: "Customers" }]);
  useEffect(() => { api.get("/migration/types").then((r) => setImportTypes(r.data)).catch(() => {}); }, []);
  const [file, setFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [replaceImages, setReplaceImages] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dupAction, setDupAction] = useState("skip");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rerunAsk, setRerunAsk] = useState(null); // {previous_import_at}
  const [dupAsk, setDupAsk] = useState(false);   // Create New with existing customers (+ opening balances)
  const [pendingForce, setPendingForce] = useState(false);
  const [approvalPin, setApprovalPin] = useState("");
  const [approvalReason, setApprovalReason] = useState("");

  const reset = () => { setStep(1); setFile(null); setParsed(null); setMapping({}); setPreview(null); setResult(null); };

  const doParse = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("data_type", dataType);
      const { data } = await api.post(`${base}/parse`, fd);
      setParsed(data); setMapping(data.suggested_mapping || {}); setStep(2);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const doPreview = async () => {
    const missing = (parsed.fields || []).filter((f) => f.required && !mapping[f.key]).map((f) => f.label);
    if (missing.length) { toast.error("Map required field(s): " + missing.join(", ")); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("data_type", dataType);
      fd.append("mapping", JSON.stringify(mapping)); fd.append("dup_action", dupAction);
      const { data } = await api.post(`${base}/preview`, fd);
      setPreview(data); setStep(3);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const doCommit = async (force = false, managerPin = null, reason = null) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("data_type", dataType);
      fd.append("mapping", JSON.stringify(mapping)); fd.append("dup_action", dupAction); fd.append("force", force ? "true" : "false");
      if (zipFile) fd.append("zip", zipFile); fd.append("replace_images", replaceImages ? "true" : "false");
      if (managerPin) fd.append("manager_pin", managerPin);
      if (reason) fd.append("reason", reason);
      const { data } = await api.post(`${base}/commit`, fd);
      if (data.committed === false && data.rerun_warning) {
        setRerunAsk({ previous_import_at: data.previous_import_at }); setBusy(false); return;
      }
      setResult(data); setStep(4);
      toast.success("Import complete");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const actionable = preview ? [...preview.ready_rows, ...preview.warning_rows] : [];
  const heldRows = preview?.held_rows || [];
  const nSkip = actionable.filter((r) => r.action === "skip").length;
  const nUpd = actionable.filter((r) => r.action === "update").length;
  const nNew = actionable.filter((r) => r.action === "create").length;
  const dupCreateRows = actionable.filter((r) => r.duplicate_create);
  const total = actionable.length;
  // Accurate verb: never say "Import N" when every row will be skipped.
  const commitLabel = total === 0 ? "Nothing to process" : nSkip === total ? `Skip ${total} existing record${total === 1 ? "" : "s"}`
    : nSkip || nUpd ? `Process ${total} record${total === 1 ? "" : "s"} (${[nNew && `${nNew} new`, nUpd && `${nUpd} update`, nSkip && `${nSkip} skip`].filter(Boolean).join(", ")})`
    : `Import ${total} record${total === 1 ? "" : "s"}`;

  const attemptCommit = (force = false) => {
    // Rerun confirmation always comes first: asking for a fresh Manager/Owner PIN before the
    // user even knows this file was already imported would mean asking for it twice.
    if (!force && preview?.rerun_warning) { setRerunAsk({ previous_import_at: preview.previous_import_at }); return; }
    if (dupCreateRows.length) { setPendingForce(force); setApprovalPin(""); setApprovalReason(""); setDupAsk(true); }
    else doCommit(force);
  };

  const downloadRejected = () => {
    const rows = (preview?.rejected_rows || result?.rejected_rows || []);
    if (!rows.length) return;
    const cols = Object.keys(rows[0]).filter((k) => k !== "issues" && k !== "status");
    const head = ["Row", ...cols, "Errors"];
    const body = rows.map((r) => [r.row, ...cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`),
      `"${(Array.isArray(r.issues) ? r.issues.join("; ") : r.issues || "").replace(/"/g, '""')}"`].join(","));
    const blob = new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "rejected_rows.csv"; a.click();
  };

  const StatusBadge = ({ s }) => {
    const map = { ready: ["bg-emerald-500/15 text-emerald-600 border-emerald-500/30", "Ready"],
      warning: ["bg-amber-500/15 text-amber-600 border-amber-500/30", "Warning"],
      held: ["bg-sky-500/15 text-sky-600 border-sky-500/30", "Held for review"],
      rejected: ["bg-rose-500/15 text-rose-600 border-rose-500/30", "Rejected"] };
    const v = map[s] || ["bg-secondary text-muted-foreground border-border", s || "—"];
    return <Badge className={`text-[10px] ${v[0]}`}>{v[1]}</Badge>;
  };

  const Stepper = () => (
    <div className="flex items-center gap-2 text-xs mb-6" data-testid="mig-stepper">
      {["Upload", "Map columns", "Preview", "Results"].map((l, i) => (
        <div key={l} className="flex items-center gap-2">
          <span className={`flex items-center justify-center w-6 h-6 rounded-full font-bold ${step >= i + 1 ? "bg-emerald-600 text-white" : "bg-secondary text-muted-foreground"}`}>{i + 1}</span>
          <span className={step >= i + 1 ? "font-semibold" : "text-muted-foreground"}>{l}</span>
          {i < 3 && <ArrowRight className="w-3 h-3 text-muted-foreground" />}
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid="migration-wizard">
      <Stepper />

      {step === 1 && (
        <Card className="p-6 space-y-5" data-testid="mig-step-upload">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">What are you importing?</p>
            <div className="flex gap-2">
              {importTypes.map(({ key: v, name: l }) => (
                <Button key={v} variant={dataType === v ? "default" : "outline"}
                  className={dataType === v ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                  onClick={() => setDataType(v)} data-testid={`mig-type-${v}`}>{l}</Button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">QuickBooks export file (CSV or XLSX)</p>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-8 cursor-pointer hover:border-emerald-500/50 transition-colors" data-testid="mig-dropzone">
              <UploadCloud className="w-8 h-8 text-muted-foreground" />
              <span className="text-sm">{file ? file.name : "Click to choose a .csv or .xlsx file"}</span>
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="mig-file-input"
                onChange={(e) => setFile(e.target.files[0])} />
            </label>
          </div>
          <Button onClick={doParse} disabled={busy || !file} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mig-parse-btn">
            {busy ? "Reading…" : "Continue"} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Card>
      )}

      {step === 2 && parsed && (
        <Card className="p-6 space-y-4" data-testid="mig-step-map">
          <p className="text-sm text-muted-foreground">We detected <b>{parsed.total_rows}</b> rows and {parsed.headers.length} columns. Map your QuickBooks columns to NEEC fields. Unmapped columns are ignored. Nothing is guessed.</p>
          <div className="space-y-2">
            {parsed.fields.map((f) => (
              <div key={f.key} className="grid grid-cols-2 gap-3 items-center">
                <span className="text-sm">{f.label}{f.required && <span className="text-rose-500"> *</span>}</span>
                <select value={mapping[f.key] || ""} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value || undefined })}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid={`mig-map-${f.key}`}>
                  <option value="">Not mapped</option>
                  {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">If a record already exists</p>
            <div className="flex gap-2">
              {[["skip", "Skip existing"], ["update", "Update existing"], ["create", "Create new"]].map(([v, l]) => (
                <Button key={v} size="sm" variant={dupAction === v ? "default" : "outline"}
                  className={dupAction === v ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                  onClick={() => setDupAction(v)} data-testid={`mig-dup-${v}`}>{l}</Button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button onClick={doPreview} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mig-preview-btn">
              {busy ? "Validating…" : "Preview"} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && preview && (
        <Card className="p-6 space-y-4" data-testid="mig-step-preview">
          <div className="grid grid-cols-4 gap-3">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-center"><p className="text-2xl font-bold text-emerald-600" data-testid="mig-count-ready">{preview.ready}</p><p className="text-xs text-muted-foreground">Ready</p></div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-center"><p className="text-2xl font-bold text-amber-600" data-testid="mig-count-warning">{preview.warnings}</p><p className="text-xs text-muted-foreground">Warnings</p></div>
            <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 text-center"><p className="text-2xl font-bold text-sky-600" data-testid="mig-count-held">{preview.held || 0}</p><p className="text-xs text-muted-foreground">Held for review</p></div>
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-center"><p className="text-2xl font-bold text-rose-600" data-testid="mig-count-rejected">{preview.rejected}</p><p className="text-xs text-muted-foreground">Rejected</p></div>
          </div>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <tbody>
                {[...preview.rejected_rows, ...heldRows, ...preview.warning_rows, ...preview.ready_rows].slice(0, 200).map((r, i) => (
                  <tr key={i} className="border-b border-border" data-testid={`mig-row-${r.row}`}>
                    <td className="p-2 w-10 text-muted-foreground">#{r.row}</td>
                    <td className="p-2"><StatusBadge s={r.status} /></td>
                    <td className="p-2 font-medium">{dataType === "products" ? `${r.sku || "-"} · ${r.name || "-"}` : dataType === "suppliers" ? `${r.name || "-"}${r.product_label ? ` → ${r.product_label}` : r.needs_product_match ? " → Needs Product Match" : ""}` : `${r.name || "-"}`}</td>
                    {dataType === "products" && <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] border ${r.product_type === "non_stock" ? "bg-sky-500/15 text-sky-500 border-sky-500/30" : "bg-emerald-500/15 text-emerald-600 border-emerald-500/30"}`} data-testid={`mig-type-${r.row}`}>{r.product_type === "non_stock" ? "Non-Stock" : "Stock"}</span></td>}
                    <td className="p-2 text-muted-foreground">{dataType === "products" ? money(r.price || 0) : (r.opening_balance ? `Bal ${money(r.opening_balance)}` : "")}</td>
                    <td className="p-2 text-muted-foreground">{Array.isArray(r.issues) ? r.issues.join("; ") : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.rejected > 0 && <Button variant="outline" size="sm" onClick={downloadRejected} data-testid="mig-download-rejected"><Download className="w-4 h-4 mr-1.5" /> Download rejected rows (CSV)</Button>}
          {heldRows.length > 0 && <p className="text-xs text-sky-600 flex items-center gap-1.5" data-testid="mig-held-note"><AlertTriangle className="w-4 h-4" /> {heldRows.length} row{heldRows.length === 1 ? "" : "s"} held for manual review (possible match or same-file duplicate) — never imported automatically. Resolve them in your file and re-upload if needed.</p>}
          {preview.rerun_warning && <p className="text-xs text-amber-600 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> This exact file appears to have been imported before ({(preview.previous_import_at || "").slice(0, 16).replace("T", " ")}). You'll be asked to confirm.</p>}
          {dataType === "customers" && actionable.some((r) => r.action === "create" && r.opening_balance > 0) && <p className="text-xs text-muted-foreground" data-testid="mig-ob-policy">Opening balances are recorded as legacy balances due on the import date (no original due date exists in the sheet). Credit terms apply to new credit sales.</p>}
          {dataType === "products" && (
            <div className="rounded-xl border border-border p-3 space-y-2 text-sm" data-testid="mig-images-block">
              <p className="font-medium">Pictures (optional)</p>
              <p className="text-xs text-muted-foreground">Rows with an Image URL (https) or Image Filename get a picture. Upload the ZIP that contains the referenced files (≤ 50 MB, ≤ 200 images, 5 MB each).</p>
              <input type="file" accept=".zip" className="block text-xs" onChange={(e) => setZipFile(e.target.files[0] || null)} data-testid="mig-zip-input" />
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={replaceImages} onCheckedChange={(v) => setReplaceImages(!!v)} data-testid="mig-replace-images" /> Replace existing pictures (otherwise kept)</label>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
            <Button onClick={() => attemptCommit(false)} disabled={busy || (preview.ready + preview.warnings === 0)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mig-commit-btn">
              {busy ? "Processing…" : commitLabel}
            </Button>
          </div>
        </Card>
      )}

      {step === 4 && result && (
        <Card className="p-6 space-y-4" data-testid="mig-step-results">
          <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="w-6 h-6" /><p className="font-head font-bold text-lg">Import complete</p></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {Object.entries(result.summary || {}).filter(([k, v]) => typeof v === "number" && v > 0).map(([k, v]) => (
              <div key={k} className="rounded-xl border border-border p-3" data-testid={`mig-summary-${k}`}>
                <p className="text-xl font-bold">{v}</p><p className="text-xs text-muted-foreground capitalize">{k.replace(/_/g, " ")}</p>
              </div>
            ))}
            <div className="rounded-xl border border-border p-3"><p className="text-xl font-bold text-rose-600">{result.rejected}</p><p className="text-xs text-muted-foreground">Rejected rows</p></div>
            {result.held > 0 && <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3" data-testid="mig-summary-held"><p className="text-xl font-bold text-sky-600">{result.held}</p><p className="text-xs text-muted-foreground">Held for review (never imported)</p></div>}
          </div>
          {(result.summary?.row_outcomes || []).some((o) => o.outcome === "failed" || o.outcome === "held") && (
            <div className="rounded-lg border border-border max-h-48 overflow-y-auto text-xs" data-testid="mig-row-outcomes">
              {result.summary.row_outcomes.filter((o) => o.outcome === "failed" || o.outcome === "held").map((o, i) => (
                <div key={i} className="p-2 border-b border-border last:border-0 flex gap-2">
                  <span className="text-muted-foreground">Row #{o.row}</span>
                  <Badge className={`text-[10px] ${o.outcome === "held" ? "bg-sky-500/15 text-sky-600 border-sky-500/30" : "bg-rose-500/15 text-rose-600 border-rose-500/30"}`}>{o.outcome}</Badge>
                  <span className="text-muted-foreground">{o.error}</span>
                </div>
              ))}
            </div>
          )}
          {result.rejected > 0 && <Button variant="outline" size="sm" onClick={downloadRejected} data-testid="mig-results-download"><Download className="w-4 h-4 mr-1.5" /> Download rejected rows (CSV)</Button>}
          <Button onClick={reset} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mig-new-import">Start another import</Button>
        </Card>
      )}
      <Dialog open={!!rerunAsk} onOpenChange={(o) => !o && setRerunAsk(null)}>
        <DialogContent className="max-w-md" data-testid="mig-rerun-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> This file was already imported</DialogTitle>
            <DialogDescription>This exact file was imported before{rerunAsk?.previous_import_at ? ` on ${String(rerunAsk.previous_import_at).slice(0, 16).replace("T", " ")}` : ""}. Running it again follows your duplicate setting ({dupAction === "skip" ? "Skip existing" : dupAction === "update" ? "Update existing" : "Create new"}). Continue?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRerunAsk(null)} data-testid="mig-rerun-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { setRerunAsk(null); attemptCommit(true); }} data-testid="mig-rerun-confirm">Import again</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={dupAsk} onOpenChange={setDupAsk}>
        <DialogContent className="max-w-md" data-testid="mig-dup-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-500" /> {dupCreateRows.length} customer{dupCreateRows.length === 1 ? "" : "s"} already {dupCreateRows.length === 1 ? "exists" : "exist"}</DialogTitle>
            <DialogDescription>Create new will make a separate customer record for each of these, alongside the existing one. This needs a fresh Manager or Owner authorization — it is recorded against each record created.
              {dupCreateRows.some((r) => r.opening_balance > 0) && <span className="block mt-2 font-semibold text-rose-600" data-testid="mig-dup-ar-warning">{dupCreateRows.filter((r) => r.opening_balance > 0).length} of them carry an opening balance: a separate A/R balance will be created in addition to the existing customer's balance.</span>}
              <span className="block mt-2">Choose Skip existing or Update existing instead if these are the same customers.</span>
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-32 overflow-y-auto text-xs space-y-1" data-testid="mig-dup-list">
            {dupCreateRows.slice(0, 50).map((r) => <li key={r.row}>Row {r.row}: <b>{r.name}</b>{r.opening_balance > 0 ? ` · opening balance ${money(r.opening_balance)}` : ""}</li>)}
          </ul>
          <div className="space-y-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason (required)</label>
              <input value={approvalReason} onChange={(e) => setApprovalReason(e.target.value)} placeholder="e.g. Household shares one phone line"
                className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" data-testid="mig-dup-reason-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Manager / Owner Authorization PIN</label>
              <input value={approvalPin} onChange={(e) => setApprovalPin(e.target.value.replace(/\D/g, ""))} type="password" inputMode="numeric" maxLength={6} placeholder="PIN (4-6 digits)"
                className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" data-testid="mig-dup-pin-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDupAsk(false)} data-testid="mig-dup-cancel">Go back</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy || approvalPin.trim().length < 4 || !approvalReason.trim()}
              onClick={() => { setDupAsk(false); doCommit(pendingForce, approvalPin.trim(), approvalReason.trim()); }} data-testid="mig-dup-confirm">
              Authorize &amp; create separate customers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ImportData() {
  return (
    <div className="space-y-6 max-w-4xl" data-testid="import-data-page">
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2">
          <FileSpreadsheet className="w-7 h-7 text-emerald-500" /> Import Data
        </h1>
        <p className="text-muted-foreground">Migrate your Products and Customers from QuickBooks (CSV or Excel) into NEEC POS.</p>
      </div>
      <MigrationWizard base="/migration" />
      <PicturesImport />
    </div>
  );
}
